'use client';

import { useState, useRef } from 'react'
import Link from 'next/link'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  ArrowRight, QrCode, Sparkles, Download, Copy, Check, 
  RefreshCw, Globe, Shield, Edit2, Trash2, ExternalLink, 
  Sliders, Calendar, Eye, Power, Plus, Search
} from 'lucide-react'
import { GeometricBackground } from '@/components/ui/geometric-background'
import { MarketingNavbar } from '@/components/layout/marketing-navbar'
import { MarketingFooter } from '@/components/layout/marketing-footer'
import { QRCodeSVG } from 'qrcode.react'
import { toast } from 'sonner'

interface DashboardQrCode {
  id: string
  title: string
  type: 'static' | 'dynamic'
  target_url: string
  fg_color: string
  bg_color: string
  include_logo: boolean
  scan_count: number
  is_active: boolean
  created_at: string
}

type QRColor = {
  name: string
  hex: string
}

const FG_COLORS: QRColor[] = [
  { name: 'Lenklyst Blue', hex: '#0066FF' },
  { name: 'Electric Purple', hex: '#8B5CF6' },
  { name: 'Mint Green', hex: '#10B981' },
  { name: 'Neon Rose', hex: '#F43F5E' },
  { name: 'Classic Black', hex: '#000000' }
]

const BG_COLORS: QRColor[] = [
  { name: 'Classic White', hex: '#FFFFFF' },
  { name: 'Dark Gray', hex: '#111827' }
]

export default function QrCodeGeneratorClient() {
  const [activeTab, setActiveTab] = useState<'dashboard' | 'customizer'>('dashboard')
  const [searchQuery, setSearchQuery] = useState('')

  // QR List State
  const [qrCodes, setQrCodes] = useState<DashboardQrCode[]>([
    {
      id: 'qr-001',
      title: 'Summer Sale Flyer',
      type: 'dynamic',
      target_url: 'https://lenklyst.com/promo/summer-2026',
      fg_color: '#0066FF',
      bg_color: '#FFFFFF',
      include_logo: true,
      scan_count: 852,
      is_active: true,
      created_at: '2026-06-05 10:30'
    },
    {
      id: 'qr-002',
      title: 'App Store Download Badge',
      type: 'dynamic',
      target_url: 'https://apps.apple.com/app/lenklyst',
      fg_color: '#8B5CF6',
      bg_color: '#FFFFFF',
      include_logo: true,
      scan_count: 1403,
      is_active: true,
      created_at: '2026-06-03 14:15'
    },
    {
      id: 'qr-003',
      title: 'Restaurant Menu Table 4',
      type: 'static',
      target_url: 'https://lenklyst.com/s/bistro-menu-pdf',
      fg_color: '#000000',
      bg_color: '#FFFFFF',
      include_logo: false,
      scan_count: 312,
      is_active: false,
      created_at: '2026-05-28 09:00'
    }
  ])

  // Customizer state inputs
  const [customTitle, setCustomTitle] = useState('New Campaign QR')
  const [customType, setCustomType] = useState<'static' | 'dynamic'>('dynamic')
  const [customUrl, setCustomUrl] = useState('https://lenklyst.com/campaign')
  const [customFg, setCustomFg] = useState('#0066FF')
  const [customBg, setCustomBg] = useState('#FFFFFF')
  const [customLogo, setCustomLogo] = useState(true)
  
  // Inline editing state
  const [editingQrId, setEditingQrId] = useState<string | null>(null)
  const [editingUrlValue, setEditingUrlValue] = useState('')

  // Copy states
  const [copiedId, setCopiedId] = useState<string | null>(null)

  // Handlers
  const handleToggleActive = (id: string) => {
    setQrCodes(prev => prev.map(qr => {
      if (qr.id === id) {
        const nextState = !qr.is_active
        toast.success(`QR "${qr.title}" is now ${nextState ? 'Active' : 'Paused'}`)
        return { ...qr, is_active: nextState }
      }
      return qr
    }))
  }

  const handleDeleteQr = (id: string) => {
    setQrCodes(prev => prev.filter(qr => qr.id !== id))
    toast.success('QR Code removed from dashboard')
  }

  const handleCopyLink = (qr: DashboardQrCode) => {
    navigator.clipboard.writeText(qr.target_url)
    setCopiedId(qr.id)
    toast.success('Target link copied to clipboard!')
    setTimeout(() => setCopiedId(null), 2000)
  }

  const handleDownloadQr = (qr: DashboardQrCode) => {
    try {
      const svgElement = document.getElementById(`qr-svg-${qr.id}`)?.querySelector('svg')
      if (!svgElement) {
        toast.error('Failed to locate QR SVG element')
        return
      }

      const svgString = new XMLSerializer().serializeToString(svgElement)
      const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' })
      const svgUrl = URL.createObjectURL(svgBlob)

      const downloadLink = document.createElement('a')
      downloadLink.href = svgUrl
      downloadLink.download = `${qr.title.toLowerCase().replace(/[^a-z0-9]/g, '-')}-qr.svg`
      document.body.appendChild(downloadLink)
      downloadLink.click()
      document.body.removeChild(downloadLink)
      URL.revokeObjectURL(svgUrl)

      toast.success('Vector SVG QR code downloaded!')
    } catch {
      toast.error('Failed to download QR code.')
    }
  }

  // Edit target URL inline
  const handleStartEdit = (qr: DashboardQrCode) => {
    setEditingQrId(qr.id)
    setEditingUrlValue(qr.target_url)
  }

  const handleSaveEdit = (id: string) => {
    if (!editingUrlValue.trim()) {
      toast.error('Target URL cannot be empty')
      return
    }

    setQrCodes(prev => prev.map(qr => {
      if (qr.id === id) {
        toast.success(`Dynamic QR destination updated instantly!`)
        return { ...qr, target_url: editingUrlValue }
      }
      return qr
    }))
    setEditingQrId(null)
  }

  // Save new QR from customizer
  const handleSaveNewQr = () => {
    if (!customTitle.trim()) {
      toast.error('Please specify a title')
      return
    }
    if (!customUrl.trim()) {
      toast.error('Please specify a target URL')
      return
    }

    const newQr: DashboardQrCode = {
      id: `qr-${Date.now()}`,
      title: customTitle,
      type: customType,
      target_url: customUrl,
      fg_color: customFg,
      bg_color: customBg,
      include_logo: customLogo,
      scan_count: 0,
      is_active: true,
      created_at: new Date().toISOString().replace('T', ' ').slice(0, 16)
    }

    setQrCodes(prev => [...prev, newQr])
    toast.success(`QR "${customTitle}" generated and saved!`)
    
    // Reset customizer
    setCustomTitle('New Campaign QR')
    setCustomUrl('https://lenklyst.com/campaign')
    
    // Redirect to dashboard
    setActiveTab('dashboard')
  }

  const filteredQrCodes = qrCodes.filter(qr => 
    qr.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    qr.target_url.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
  }
  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  }

  return (
    <div className="relative min-h-screen bg-black overflow-x-clip selection:bg-primary/30 text-white">
      <GeometricBackground />
      <MarketingNavbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 px-6 max-w-[1400px] mx-auto z-10 flex flex-col items-center">
        <motion.div 
          className="text-center space-y-6 max-w-3xl"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div variants={itemVariants} className="flex items-center gap-2 justify-center">
            <span className="px-3 py-1 bg-primary/20 text-primary text-[10px] font-bold tracking-widest uppercase rounded-full">
              Dynamic QR Creator
            </span>
          </motion.div>

          <motion.h1 variants={itemVariants} className="text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tighter uppercase leading-[1.1]">
            Dynamic QR Code Generator<br />
            <span className="text-gray-500">Brand Custom Creator for</span><br />
            Business & Marketing
          </motion.h1>

          <motion.p variants={itemVariants} className="text-base sm:text-lg text-white/50 font-light max-w-xl mx-auto leading-relaxed">
            Generate high-resolution dynamic QR codes. Swap destination URLs instantly without reprinting, customize layouts, embed logos, and measure scans.
          </motion.p>
        </motion.div>

        {/* Sandbox Dashboard Container */}
        <motion.div 
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.8 }}
          className="w-full max-w-5xl mt-12 bg-[#0A0A0A]/80 border border-white/10 rounded-3xl backdrop-blur-xl shadow-[0_0_80px_rgba(0,102,255,0.05)] overflow-hidden"
        >
          {/* Dashboard Header Bar */}
          <div className="px-6 py-4 border-b border-white/[0.08] flex flex-col sm:flex-row items-start sm:items-center justify-between bg-white/[0.02] gap-4">
            <div className="flex items-center gap-2">
              <QrCode className="w-4 h-4 text-primary" />
              <span className="text-sm font-semibold uppercase tracking-wider">QR Codes Hub Workspace</span>
            </div>
            
            {activeTab === 'dashboard' && (
              <button
                onClick={() => setActiveTab('customizer')}
                className="flex items-center gap-1.5 px-4 py-2 bg-primary hover:bg-white text-white hover:text-black rounded-xl text-xs font-bold transition-all duration-300"
              >
                <Plus className="w-3.5 h-3.5" /> Customize QR
              </button>
            )}
          </div>

          {/* Workspace Tabs Navigation */}
          <div className="flex border-b border-white/[0.08] px-4 pt-3 bg-white/[0.01]">
            {[
              { id: 'dashboard', label: 'Saved QR Codes' },
              { id: 'customizer', label: 'QR Designer Customizer' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex-1 pb-3 text-[10px] sm:text-xs font-bold uppercase tracking-wider transition-all border-b-2 ${
                  activeTab === tab.id 
                    ? 'text-white border-primary' 
                    : 'text-white/40 border-transparent hover:text-white/60'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Workspace Panels */}
          <div className="p-6">
            <AnimatePresence mode="wait">

              {/* ════ QR CODES DASHBOARD VIEW ════ */}
              {activeTab === 'dashboard' && (
                <motion.div
                  key="qr-dashboard-tab"
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -5 }}
                  className="space-y-6"
                >
                  <div className="relative max-w-md">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/25" />
                    <input
                      type="text"
                      placeholder="Search QR codes or links..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-11 pr-4 py-2.5 bg-white/[0.04] border border-white/[0.06] rounded-xl text-xs text-white placeholder-white/25 focus:outline-none"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {filteredQrCodes.map((qr) => (
                      <div 
                        key={qr.id}
                        className="group flex flex-col sm:flex-row gap-5 p-5 bg-white/[0.02] border border-white/5 hover:border-white/10 rounded-2xl relative transition-all duration-300"
                      >
                        {/* QR Code Container */}
                        <div 
                          id={`qr-svg-${qr.id}`}
                          className="p-3 bg-white rounded-xl shadow-md flex items-center justify-center shrink-0 w-fit self-center"
                        >
                          <QRCodeSVG
                            value={qr.target_url}
                            size={100}
                            fgColor={qr.fg_color}
                            bgColor={qr.bg_color}
                            level="Q"
                            includeImage={qr.include_logo}
                            imageSettings={{
                              src: '/logo.png',
                              x: undefined,
                              y: undefined,
                              height: 18,
                              width: 18,
                              excavate: true,
                            }}
                          />
                        </div>

                        {/* QR Details */}
                        <div className="flex-1 flex flex-col justify-between space-y-4">
                          <div className="space-y-2">
                            <div className="flex justify-between items-start">
                              <div>
                                <h3 className="text-sm font-bold text-white line-clamp-1">{qr.title}</h3>
                                <span className={`inline-block px-2 py-0.5 rounded text-[8px] font-mono font-bold mt-1 border uppercase tracking-wider ${
                                  qr.type === 'dynamic' 
                                    ? 'bg-blue-500/10 text-blue-400 border-blue-500/25' 
                                    : 'bg-zinc-800 text-white/40 border-white/5'
                                }`}>
                                  {qr.type}
                                </span>
                              </div>

                              <div className="flex items-center gap-1.5">
                                <button
                                  onClick={() => handleToggleActive(qr.id)}
                                  className={`p-1.5 rounded-lg transition-all ${qr.is_active ? 'bg-emerald-500/10 text-emerald-400' : 'bg-white/5 text-white/30'}`}
                                  title={qr.is_active ? 'QR Active' : 'QR Paused'}
                                >
                                  <Power className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={() => handleDeleteQr(qr.id)}
                                  className="p-1.5 text-white/20 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all"
                                  title="Delete QR"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>

                            {/* Destination link editing field */}
                            {editingQrId === qr.id ? (
                              <div className="space-y-2 pt-1">
                                <span className="text-[8px] font-mono text-white/40 uppercase">Edit Target URL</span>
                                <div className="flex gap-2">
                                  <input
                                    type="text"
                                    value={editingUrlValue}
                                    onChange={(e) => setEditingUrlValue(e.target.value)}
                                    className="flex-1 px-2.5 py-1 bg-white/5 border border-white/10 rounded-lg text-xs font-mono focus:outline-none"
                                  />
                                  <button
                                    onClick={() => handleSaveEdit(qr.id)}
                                    className="px-2 py-1 bg-primary text-white rounded-lg text-xs font-bold"
                                  >
                                    Save
                                  </button>
                                  <button
                                    onClick={() => setEditingQrId(null)}
                                    className="px-2 py-1 bg-white/5 text-white/60 rounded-lg text-xs"
                                  >
                                    X
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="space-y-1">
                                <span className="text-[8px] font-mono text-white/30 uppercase block">Target Redirect</span>
                                <div className="flex items-center gap-1.5 text-white/50 text-[11px] font-mono">
                                  <Globe className="w-3 h-3 text-primary" />
                                  <span className="truncate max-w-[150px]">{qr.target_url}</span>
                                  {qr.type === 'dynamic' && (
                                    <button 
                                      onClick={() => handleStartEdit(qr)}
                                      className="p-1 text-white/30 hover:text-white"
                                      title="Edit target link"
                                    >
                                      <Edit2 className="w-3 h-3" />
                                    </button>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>

                          <div className="flex items-center justify-between border-t border-white/[0.04] pt-3 text-[10px] font-mono">
                            <div>
                              <span className="text-white/20 block text-[8px] uppercase">Scan Stats</span>
                              <span className="text-sm font-black text-white">{qr.scan_count.toLocaleString()} scans</span>
                            </div>

                            <div className="flex gap-1.5">
                              <button
                                onClick={() => handleCopyLink(qr)}
                                className="p-1.5 bg-white/[0.03] hover:bg-white/[0.08] text-white/60 hover:text-white rounded-lg border border-white/[0.05]"
                                title="Copy Target Link"
                              >
                                {copiedId === qr.id ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Copy className="w-3.5 h-3.5" />}
                              </button>
                              <button
                                onClick={() => handleDownloadQr(qr)}
                                className="flex items-center gap-1 px-2.5 py-1.5 bg-white/[0.03] hover:bg-white/[0.08] text-white/70 hover:text-white rounded-lg border border-white/[0.05] text-[9px] font-bold"
                              >
                                <Download className="w-3 h-3" /> SVG
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    {filteredQrCodes.length === 0 && (
                      <div className="col-span-2 py-12 text-center text-white/20 font-light">
                        No QR codes found matching your search.
                      </div>
                    )}
                  </div>
                </motion.div>
              )}

              {/* ════ QR CUSTOMIZER VIEW ════ */}
              {activeTab === 'customizer' && (
                <motion.div
                  key="qr-customizer-tab"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0 }}
                  className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch"
                >
                  {/* Customizer Settings Form */}
                  <div className="lg:col-span-7 space-y-6 flex flex-col justify-between">
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <label className="text-xs font-mono text-white/40 uppercase block">Campaign Name</label>
                          <input
                            type="text"
                            value={customTitle}
                            onChange={(e) => setCustomTitle(e.target.value)}
                            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-xl text-xs text-white focus:outline-none focus:border-primary/60 transition-all font-light"
                          />
                        </div>

                        <div className="space-y-1.5">
                          <label className="text-xs font-mono text-white/40 uppercase block">QR Code Mode</label>
                          <div className="grid grid-cols-2 gap-2">
                            {(['dynamic', 'static'] as const).map(t => (
                              <button
                                key={t}
                                type="button"
                                onClick={() => setCustomType(t)}
                                className={`py-2 px-2.5 rounded-xl text-[10px] font-bold border transition-all ${
                                  customType === t 
                                    ? 'border-primary bg-primary/10 text-white' 
                                    : 'border-white/5 bg-white/[0.02] text-white/40 hover:border-white/10'
                                }`}
                              >
                                {t.toUpperCase()}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <label className="text-xs font-mono text-white/40 uppercase block">Redirect Target Destination</label>
                        <input
                          type="text"
                          value={customUrl}
                          onChange={(e) => setCustomUrl(e.target.value)}
                          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-xl text-xs text-white focus:outline-none focus:border-primary/60 transition-all font-mono"
                        />
                      </div>

                      {/* Foreground colors */}
                      <div className="space-y-2.5">
                        <label className="text-xs font-mono text-white/40 uppercase block">Foreground Palette</label>
                        <div className="flex flex-wrap gap-2">
                          {FG_COLORS.map(c => (
                            <button
                              key={c.hex}
                              onClick={() => setCustomFg(c.hex)}
                              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[10px] font-semibold border transition-all ${
                                customFg === c.hex 
                                  ? 'border-primary bg-primary/10 text-white' 
                                  : 'border-white/5 bg-white/[0.01] text-white/40 hover:border-white/10'
                              }`}
                            >
                              <div className="w-3 h-3 rounded-full border border-white/10" style={{ backgroundColor: c.hex }} />
                              {c.name}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Background colors */}
                      <div className="space-y-2.5">
                        <label className="text-xs font-mono text-white/40 uppercase block">Background Tint</label>
                        <div className="flex flex-wrap gap-2">
                          {BG_COLORS.map(c => (
                            <button
                              key={c.hex}
                              onClick={() => setCustomBg(c.hex)}
                              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[10px] font-semibold border transition-all ${
                                customBg === c.hex 
                                  ? 'border-primary bg-primary/10 text-white' 
                                  : 'border-white/5 bg-white/[0.01] text-white/40 hover:border-white/10'
                              }`}
                            >
                              <div className="w-3 h-3 rounded-full border border-white/10" style={{ backgroundColor: c.hex }} />
                              {c.name}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Logo embed checkbox */}
                      <div className="flex items-center justify-between bg-white/[0.02] border border-white/5 p-3 rounded-xl">
                        <div>
                          <span className="text-xs font-semibold text-white block">Embed Center Logo icon</span>
                          <span className="text-[10px] text-white/40 block">Aligns brand symbol inside code structure</span>
                        </div>
                        <input
                          type="checkbox"
                          checked={customLogo}
                          onChange={(e) => setCustomLogo(e.target.checked)}
                          className="w-4 h-4 accent-primary rounded bg-white/5 border-white/10"
                        />
                      </div>
                    </div>

                    <div className="flex gap-2 pt-4 border-t border-white/5">
                      <button
                        onClick={handleSaveNewQr}
                        className="flex-1 py-3 bg-primary hover:bg-primary/95 text-white font-bold rounded-xl text-xs transition-all"
                      >
                        Save & Generate Code
                      </button>
                      <button
                        onClick={() => setActiveTab('dashboard')}
                        className="py-3 px-5 bg-white/5 hover:bg-white/10 text-white rounded-xl text-xs"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>

                  {/* Customizer Realtime Preview Panel */}
                  <div className="lg:col-span-5 bg-background/30 border border-white/10 rounded-3xl p-6 backdrop-blur-xl flex flex-col items-center justify-center text-center">
                    <div className="w-full flex items-center justify-between border-b border-white/5 pb-3 mb-6">
                      <div className="flex items-center gap-1.5 text-[10px] font-mono text-white/40 uppercase">
                        <Eye className="w-3.5 h-3.5" /> Realtime Preview
                      </div>
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    </div>

                    <div className="p-5 bg-white rounded-2xl shadow-xl flex items-center justify-center border border-white/10">
                      <QRCodeSVG
                        value={customUrl || 'https://lenklyst.com'}
                        size={150}
                        fgColor={customFg}
                        bgColor={customBg}
                        level="Q"
                        includeImage={customLogo}
                        imageSettings={{
                          src: '/logo.png',
                          x: undefined,
                          y: undefined,
                          height: 28,
                          width: 28,
                          excavate: true,
                        }}
                      />
                    </div>

                    <div className="mt-5 space-y-1">
                      <span className="text-xs font-bold text-white">{customTitle}</span>
                      <p className="text-[9px] text-white/40 font-mono">
                        {customType.toUpperCase()} • {customFg} on {customBg === 'transparent' ? 'TRANSPARENT' : customBg}
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}

            </AnimatePresence>
          </div>
        </motion.div>
      </section>

      {/* Feature Grid */}
      <section className="py-20 border-t border-white/5 relative z-10">
        <div className="max-w-[1400px] mx-auto px-6 grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/5 space-y-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <RefreshCw className="w-5 h-5 text-primary" />
            </div>
            <h3 className="text-lg font-semibold uppercase tracking-tight">On-The-Fly Target Swapping</h3>
            <p className="text-sm text-white/50 font-light leading-relaxed">
              Print once, redirect forever. Change the destination URL of your dynamic QR codes at any time from your dashboard without changing the QR layout.
            </p>
          </div>
          <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/5 space-y-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Globe className="w-5 h-5 text-primary" />
            </div>
            <h3 className="text-lg font-semibold uppercase tracking-tight">Comprehensive Scan Analytics</h3>
            <p className="text-sm text-white/50 font-light leading-relaxed">
              Track scans by time, date, GPS coordinates, device operating systems, and source referrer. Build a detailed model of offline campaign performance.
            </p>
          </div>
          <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/5 space-y-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Shield className="w-5 h-5 text-primary" />
            </div>
            <h3 className="text-lg font-semibold uppercase tracking-tight">High Contrast Vector Formats</h3>
            <p className="text-sm text-white/50 font-light leading-relaxed">
              Download your QR codes as lightweight vector SVGs. Scale to billboard sizes or shrink for product labels without losing crisp scanning lines.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 border-t border-white/5 text-center relative z-10 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,102,255,0.05),transparent_70%)] blur-[100px] pointer-events-none" />
        <h2 className="text-3xl sm:text-5xl font-bold uppercase tracking-tighter">Connect offline to online</h2>
        <p className="text-white/40 text-sm max-w-md mx-auto mt-4 font-light">Create custom dynamic QR codes with pixel tracking, built-in logos, and redirect triggers.</p>
        <Link 
          href="/auth/sign-up" 
          className="inline-flex items-center gap-2 mt-8 px-8 py-3.5 bg-primary text-white font-bold rounded-full hover:scale-105 transition-all shadow-[0_0_30px_rgba(0,102,255,0.3)] active:scale-95"
        >
          Create Dynamic QR Code <ArrowRight className="w-4 h-4" />
        </Link>
      </section>

      <MarketingFooter />
    </div>
  )
}
